import { X, Search, Edit, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AddFamilyMemberModalProps {
  onClose: () => void;
  onSelectMethod: (method: 'search' | 'manual' | 'ancestry-did') => void;
  relationship: string;
  relationshipDisplayLabel?: string;
}

export function AddFamilyMemberModal({
  onClose,
  onSelectMethod,
  relationship,
  relationshipDisplayLabel,
}: AddFamilyMemberModalProps) {
  const { t } = useTranslation();
  const displayRelationship = relationshipDisplayLabel ?? relationship;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2
                className="text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('family.modals.addFamilyMemberTitle')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('family.modals.adding')}: {displayRelationship}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 flex-1 min-h-0 overflow-auto">
          <p
            className="text-gray-600 dark:text-white/70 mb-6"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('family.modals.chooseHowToAdd')}
          </p>

          <div className="space-y-4">
            <button
              onClick={() => onSelectMethod('search')}
              className="w-full bg-white dark:bg-[#0F0E16] border-2 border-gray-200 dark:border-white/20 rounded-xl p-6 hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-lg transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                  <Search className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3
                    className="text-lg mb-2 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    🔍 {t('family.modals.searchPetsoDatabase')}
                  </h3>
                  <p
                    className="text-sm text-gray-600 dark:text-white/70 mb-2"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.findExistingPets')}
                  </p>
                  <div className="inline-flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                    ✅ {t('family.modals.recommendedAutoVerified')}
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() => onSelectMethod('manual')}
              className="w-full bg-white dark:bg-[#0F0E16] border-2 border-gray-200 dark:border-white/20 rounded-xl p-6 hover:border-purple-500 dark:hover:border-purple-400 hover:shadow-lg transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                  <Edit className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3
                    className="text-lg mb-2 text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    ✍️ {t('family.modals.addManually')}
                  </h3>
                  <p
                    className="text-sm text-gray-600 dark:text-white/70 mb-2"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.enterDetailsManually')}
                  </p>
                  <div className="inline-flex items-center gap-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded">
                    🟡 {t('family.modals.ownerAddedUnverified')}
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() => onSelectMethod('ancestry-did')}
              className="w-full bg-white dark:bg-[#0F0E16] border-2 border-gray-200 dark:border-white/20 rounded-xl p-6 hover:border-indigo-500 dark:hover:border-indigo-400 hover:shadow-lg transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/50 transition-colors">
                  <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1">
                  <h3
                    className="text-lg mb-2 text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    🆔 {t('family.modals.createAncestryDid')}
                  </h3>
                  <p
                    className="text-sm text-gray-600 dark:text-white/70 mb-2"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.createPlaceholderIdentity')}
                  </p>
                  <div className="inline-flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                    🔒 {t('family.modals.futureProofCanBeLinked')}
                  </div>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
            <div
              className="text-sm text-blue-900 dark:text-blue-100"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <div className="mb-1">
                💡 {t('family.modals.buildingAncestry')}
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-300">
                {t('family.modals.familyTreesOptional')}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
